import { ASPIRE_HOSTED } from '@Constants/runtimeConstants';
import { CookieEnum } from '@Constants/StorageKeys';
import { type ApiUserPartnershipInformation } from '@Model';
import { isNullOrEmpty } from '@synqo/frontend-component-library';
import { useQueryClient } from '@tanstack/react-query';
import { type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useCookie } from 'react-use';
import { SessionContext } from './SessionContext';
import { emptySession, type AuthSession, type Session, type SessionContextPayload } from './types';
import { useStoredSession } from './useStoredSession';

const cookieAttributes: Cookies.CookieAttributes = {
    expires: 365,
    secure: !ASPIRE_HOSTED,
    sameSite: 'strict',
};

export function SessionProvider({ children }: PropsWithChildren) {
    const { storedAuthSession, setStoredAuthSession } = useStoredSession();
    const [partnershipId, setPartnershipId, clearPartnershipId] = useCookie(CookieEnum.Partnership);
    const [partnerships, setPartnerships] = useState<ApiUserPartnershipInformation[]>([]);

    const queryClient = useQueryClient();

    const updatePartnershipCookie = useCallback(
        (value: string) => {
            if (value === partnershipId) {
                return;
            }
            queryClient.clear();
            if (isNullOrEmpty(value)) {
                clearPartnershipId();
            } else {
                setPartnershipId(value, cookieAttributes);
            }
        },
        [partnershipId, queryClient, setPartnershipId, clearPartnershipId],
    );

    const updateSession = useCallback(
        (value: Partial<Session> | null) => {
            const newStoredAuthSession: AuthSession =
                value === null
                    ? emptySession
                    : {
                          ...storedAuthSession,
                          ...value,
                      };

            setStoredAuthSession(newStoredAuthSession);
            updatePartnershipCookie(newStoredAuthSession.partnershipId);
        },
        [storedAuthSession, setStoredAuthSession, updatePartnershipCookie],
    );

    const sessionContextPayload = useMemo<SessionContextPayload>(
        () => ({
            current: {
                ...storedAuthSession,
                partnershipId: partnershipId ?? '',
            },
            update: updateSession,
            partnerships,
            setPartnerships,
        }),
        [storedAuthSession, partnershipId, updateSession, partnerships],
    );

    return <SessionContext.Provider value={sessionContextPayload}>{children}</SessionContext.Provider>;
}
